import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isUazapiContactsSyncTerminal } from "@/lib/uazapi-contacts-sync";

/**
 * Notifica quando jobs de sync de contatos WhatsApp terminam (mesmo com o modal de import fechado).
 */
export function useUazapiContactsSyncWatcher(workspaceId: string | undefined) {
  const { toast } = useToast();
  const notifiedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!workspaceId) return;

    const channel = supabase
      .channel(`uazapi-contacts-sync:${workspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "uazapi_contacts_sync_jobs",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        (payload) => {
          const row = payload.new as {
            id?: string;
            status?: string;
            created_total?: number;
            skipped_existing_total?: number;
            last_error?: string | null;
          };
          const id = row.id;
          const status = row.status;
          if (!id || !status || !isUazapiContactsSyncTerminal(status)) return;
          if (notifiedRef.current.has(id)) return;
          notifiedRef.current.add(id);

          if (status === "completed") {
            const created = row.created_total ?? 0;
            const skipped = row.skipped_existing_total ?? 0;
            toast({
              title: "Sincronização do WhatsApp concluída",
              description:
                created > 0
                  ? `${created} novo(s) lead(s) criado(s).${skipped > 0 ? ` ${skipped} já estavam no CRM.` : ""}`
                  : skipped > 0
                    ? `Nenhum lead novo. ${skipped} contato(s) já estavam no CRM.`
                    : "Nenhum contato novo para importar.",
            });
          } else if (status === "failed") {
            toast({
              title: "Sincronização do WhatsApp falhou",
              description: row.last_error || "Tente novamente em alguns minutos.",
              variant: "destructive",
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workspaceId, toast]);
}
